import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { User } from '../models/user';
import { Study } from '../models/study';
import { Answersheet } from '../models/answersheet';
import { BasicError, Error403 } from '../models/error/error';
import {
  QuestionnaireElement, QuestionnaireElementHeadline, QuestionnaireElementYesNo,
  QuestionnaireElementSlider, QuestionnaireElementSingleChoice, QuestionnaireElementTextDate,
  QuestionnaireElementMultipleChoice, QuestionnaireElementTextString,
  QuestionnaireElementTextArea
} from '../models/questionnaire-element';

@Injectable()
export class HCPStatisticService {
  private collaboratingStudiesUrl = '/api/v1/editor/studies';
  private collaboratingReplay: Subject<any> = null; // ReplaySubject of get CollaboratingStudies
  private collaboratingStudiesThreshold = 0; // Until this time the cached collaborating studies will be returned
  private collaboratingStudiesThresholdAddTime = 4 * 1000; // 4 minutes threshold should be enough
  private collaboratingStudiesLoaded = false; // Set is true if collaborating studies was loaded once
  private collaboratingStudiesOnload = false; // If a request is currently loading
  private collaboratingStudiesUnauthorized = false; // Set is true if unauthorized was returned (403)
  private collaboratingStudies: Study[] = [];
  private amountCollaboratingStudies: Number = 0;


  private collaboratorsUrl = '/api/v1/studies/##id##/collaborators';
  private collaboratorsThreshold = {};
  private collaboratorsThresholdAddTime: number = 4 * 1000;
  private collaboratorsLoaded: Boolean = false;
  private collaboratorsOnload = {};
  private amountCollaborators = {};
  private collaborators = {};
  private collaboratorsReplay = {};


  private membersUrl = '/api/v1/studies/##id##/members';
  private membersThreshold = {};
  private membersThresholdAddTime = 4 * 1000;
  private members = {};
  private amountMembers = {};
  private membersReplay = {};
  private membersOnload = {};


  private amountAnswersheetsUrl = '/api/v1/studies/##id##/members/##mid##/answersheets';
  private amountAnswersheetsReplay = {};


  constructor(private communication: CommunicationService) { }

  /**
   * This function returns all collaboratingStudies of the current user.
   * @return ReplaySubject<any> - can contain error
   */
  public getCollaboratingStudies(currentPage: number = 1): Subject<any> {
    if (this.collaboratingReplay === null) {
      this.collaboratingReplay = new Subject();
    }

    if (this.collaboratingStudiesLoaded && !this.collaboratingStudiesUnauthorized &&
      this.collaboratingStudiesThreshold > (new Date().getTime())) {
      // Returning cached data of collaborating studies
      const tmpReplay = new ReplaySubject(1);
      tmpReplay.next(this.collaboratingStudies);
      this.collaboratingStudiesLoaded = false;
      return tmpReplay;
    }

    if (this.collaboratingStudiesOnload) {
      return this.collaboratingReplay;
    } else {
      this.collaboratingStudiesOnload = true;
    }

    // No threshold data found, the data will be fetched from api
    const request = this.communication.get(this.collaboratingStudiesUrl + '?page=' + currentPage + '&limit=0');
    request.subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPStatisticService---: User is not authorized to get collaborating studies.');
          this.collaboratingStudiesUnauthorized = true;
        }

        this.collaboratingStudiesThreshold = 0;
        this.collaboratingReplay.next(json);
        this.collaboratingStudiesOnload = false;
        return;
      }

      // No Error occured, user is role of editor, try to map data into list objects
      this.amountCollaboratingStudies = json.meta.pagination.total;
      const total: number = json.meta.pagination.total_pages;
      const retArr: Study[] = [];

      for (let i = 0; i < json.data.length; i++) {
        const stdJSON = json.data[i];
        const study = new Study(stdJSON.id);
        if (stdJSON.attributes.name) { study.setName(stdJSON.attributes.name) };
        if (stdJSON.attributes.title) { study.setTitle(stdJSON.attributes.title) };
        if (stdJSON.attributes.description) { study.setDescription(stdJSON.attributes.description) };
        if (stdJSON.attributes.accesstype) { study.setAccesstype(stdJSON.attributes.accesstype) };
        if (stdJSON.attributes.starts_at) { study.setStarts_at(stdJSON.attributes.starts_at) };
        if (stdJSON.attributes.ends_at) { study.setEnds_at(stdJSON.attributes.ends_at) };
        if (stdJSON.attributes.is_private) { study.setIs_private(stdJSON.attributes.is_private) };
        (stdJSON.attributes.is_running) ? study.setIs_running(true) : study.setIs_running(false);
        retArr.push(study);
      }

      // Setting all vars
      this.collaboratingStudies = this.collaboratingStudies.concat(retArr);
      if (currentPage >= total) {
        this.collaboratingStudiesThreshold = new Date().getTime() + this.collaboratingStudiesThresholdAddTime;
        this.collaboratingStudiesLoaded = true;
      } else {
        this.getCollaboratingStudies((currentPage + 1));
      }
      this.collaboratingStudiesUnauthorized = false;
      this.collaboratingStudiesOnload = false;
      this.collaboratingReplay.next(retArr);
    });

    return this.collaboratingReplay;
  }

  /**
   * This function retrieves the actual amount of collaborators
   * @param studyId studyId of study
   * @param currentPage the current page that should be loaded
   * @return object of amount collaborators -> {
   *  'count': 2, // total amount of collaborators
   *  'studyOwners': 1, // amount of studyOwners
   *  'collaborators': 1, // amount of normal co-workers
   * }
   */
  public getAmountCollaborators(studyId: String, currentPage: number = 1): Subject<any> {
    if (this.collaboratorsReplay[studyId.toString()] === undefined || this.collaboratorsReplay[studyId.toString()] === null) {
      this.collaboratorsReplay[studyId.toString()] = new Subject();
    }

    // Checking Threshold
    if (this.collaboratorsLoaded && this.collaboratorsThreshold[studyId.toString()] > (new Date().getTime())) {
      const tmpReplay = new ReplaySubject(1);
      tmpReplay.next(this.amountCollaborators[studyId.toString()]);
      return tmpReplay;
    } else {
      this.collaboratorsLoaded = false;
    }

    if (this.collaboratorsOnload[studyId.toString()]) {
      return this.collaboratorsReplay[studyId.toString()];
    } else {
      this.collaboratorsOnload[studyId.toString()] = true;
    }

    // Getting data from api
    const request = this.communication.get((this.collaboratorsUrl.replace('##id##', studyId.toString())) + '?page='
      + currentPage + '&limit=0');
    request.subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPStatisticService---: User is not authorized to get collaborators [getAmountCollaborators].');
          this.collaboratingStudiesUnauthorized = true;
        }

        this.collaboratorsThreshold[studyId.toString()] = 0;
        this.collaboratorsReplay[studyId.toString()].next(json);
        this.collaboratorsOnload[studyId.toString()] = false;
        return;
      }

      // No Error occured, user is role of editor, try to map data into list objects

      // Initialize the vars
      this.amountCollaborators[studyId.toString()] = {
        'count': 0,
        'studyOwners': 0,
        'collaborators': 0
      };

      this.collaborators[studyId.toString()] = [];

      this.amountCollaborators[studyId.toString()].count = json.meta.pagination.total;
      const total: number = json.meta.pagination.total_pages;

      // Getting all collaborators
      for (let i = 0; i < json.data.length; i++) {
        const js = json.data[i];
        const user = new User(js.id);

        if (js.attributes.name) { user.setName(js.attributes.name) };
        if (js.attributes.email) { user.setEmail(js.attributes.email) };
        if (js.attributes.firstname) { user.setFirstName(js.attributes.firstname) };
        if (js.attributes.lastname) { user.setLastName(js.attributes.lastname) };
        if (js.attributes.picture) { user.setPicture(js.attributes.picture) };
        if (js.attributes.role) { user.setRole(js.attributes.role) };

        this.collaborators[studyId.toString()].push(user);

        if (js.attributes.role && js.attributes.role === 'Study Owner') {
          this.amountCollaborators[studyId.toString()].studyOwners++;
        } else {
          this.amountCollaborators[studyId.toString()].collaborators++;
        }
      }

      if (currentPage >= total) {
        this.collaboratorsThreshold[studyId.toString()] = new Date().getTime() + this.collaboratorsThresholdAddTime;
        this.collaboratorsLoaded = true;
        this.collaboratorsOnload[studyId.toString()] = false;
        this.collaboratorsReplay[studyId.toString()].next(this.amountCollaborators[studyId.toString()]);
      } else {
        this.getAmountCollaborators(studyId, (currentPage++));
      }
    });

    return this.collaboratorsReplay[studyId.toString()];
  }

  /**
   * This function calculates the actual amount of memebers of a study
   * @param studyId id of study
   * @param currentPage the current page that should be loaded
   * @return number of members of study
   */
  public getAmountMembers(studyId: String, currentPage: number = 1): Subject<any> {
    if (this.membersReplay[studyId.toString()] === undefined || this.membersReplay[studyId.toString()] === null) {
      this.membersReplay[studyId.toString()] = new Subject();
    }

    // Checking Threshold
    if (this.membersThreshold[studyId.toString()] > (new Date().getTime())) {
      const tmpReplay = new ReplaySubject(1);
      tmpReplay.next(this.amountMembers[studyId.toString()]);
      return tmpReplay;
    }

    if (this.membersOnload[studyId.toString()] && currentPage === 1) {
      return this.membersReplay[studyId.toString()];
    } else {
      this.membersOnload[studyId.toString()] = true;
    }

    // Getting data from api
    const request = this.communication.get((this.membersUrl.replace('##id##', studyId.toString())) + '?page=' + currentPage + '&limit=0');
    request.subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPStatisticService---: User is not authorized to get amount members [getAmountMembers].');
          this.collaboratingStudiesUnauthorized = true;
        }
        console.log('---HCPStatisticService---: Error occured, threshold will be set to 0 [getAmountMembers].')
        this.membersThreshold[studyId.toString()] = 0;
        this.membersOnload[studyId.toString()] = false;
        this.membersReplay[studyId.toString()].next(json);
        return;
      }

      // No Error occured, user is role of editor, try to map data into list objects

      // Initialize the members array
      this.members[studyId.toString()] = [];

      this.amountMembers[studyId.toString()] = json.meta.pagination.total;
      const total: number = json.meta.pagination.total_pages;

      // Getting all members
      for (let i = 0; i < json.data.length; i++) {
        const js = json.data[i];
        const user = new User(js.id);

        if (js.attributes.name) { user.setName(js.attributes.name) };
        if (js.attributes.email) { user.setEmail(js.attributes.email) };
        if (js.attributes.firstname) { user.setFirstName(js.attributes.firstname) };
        if (js.attributes.lastname) { user.setLastName(js.attributes.lastname) };
        if (js.attributes.picture) { user.setPicture(js.attributes.picture) };
        if (js.attributes.role) { user.setRole(js.attributes.role) };

        this.members[studyId.toString()].push(user);
      }

      if (currentPage >= total) {
        this.membersThreshold[studyId.toString()] = new Date().getTime() + this.membersThresholdAddTime;
        this.membersOnload[studyId.toString()] = false;
        this.membersReplay[studyId.toString()].next(this.amountMembers[studyId.toString()]);
      } else {
        currentPage = currentPage + 1;
        this.getAmountMembers(studyId, currentPage);
      }
    });

    return this.membersReplay[studyId.toString()];
  }

  /**
   * This function returns the amount of answersheets of an user
   * @param studyId id of study
   * @param questionaireId [optional] id of questionaire (if null then all answers will be counted)
   */
  public getAmountAnswersheets(studyId: String, questionaireId?: String) {
    // Create replay if not already exist
    const id = studyId + '-' + questionaireId;
    if (!this.amountAnswersheetsReplay[id]) {
      this.amountAnswersheetsReplay[id] = new Subject();
    } else {
      return this.amountAnswersheetsReplay[id];
    }

    // Get members of study
    this.getAmountMembers(studyId).subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPStatisticService---: User is not authorized to get members [getAmountAnswersheets].');
        }

        this.amountAnswersheetsReplay[id].next(json);
        return;
      }

      // All members are in list of members
      let count = 0;
      let amount = 0;
      for (let i = 0; i < this.members[studyId.toString()].length; i++) {
        this.getAmountAnswersheetsOfMember(studyId, this.members[studyId.toString()][i], questionaireId).subscribe(data => {
          count++;
          if (data instanceof Number) {
            amount = amount + data.valueOf();
          }

          if (count >= this.members[studyId.toString()].length) {
            this.amountAnswersheetsReplay[id].next(amount);
            this.amountAnswersheetsReplay[id] = null;
          }
        });
      }
    });



    return this.amountAnswersheetsReplay[id];
  }

  /**
   * This function gets the amount of answersheets of an member
   * @param studyId // StudyId of hcpGroup
   * @param member // answersheets of this user
   * @param questionaireId // questionaireId of questionaire [optional]
   */
  public getAmountAnswersheetsOfMember(studyId, member, questionaireId?: String) {
    const replay = new Subject();

    // create url
    let url = this.amountAnswersheetsUrl;
    url = url.replace('##id##', studyId.toString());
    url = url.replace('##mid##', member.getID());
    if (questionaireId) { url += '?questionaire_id=' + questionaireId };

    this.communication.get(url).subscribe(json => {
      // Check if an error returned
      if (json instanceof BasicError) {
        // Error was returned, check if it was a Error403
        if (json instanceof Error403) {
          // User isn't authorized to fetch data, consider user as non-editor
          console.log('---HCPStatisticService---: User is not authorized to get members [getAmountAnswersheetsOfMember].');
        }

        replay.next(json);
        return;
      }


      if (json.meta.pagination.total !== undefined && json.meta.pagination.total !== null) {
        replay.next(json.meta.pagination.total);
      } else {
        replay.next(0);
      }
    });

    return replay;
  }

  /**
   * Calculating the weekdays of all users
   * @param answers Answersheet[] of users
   */
  public calcAverageWeekdays(answers: Array<Answersheet>) {
    const weekday = [0, 0, 0, 0, 0, 0, 0];
    const date = new Date();

    for (let i = 0; i < answers.length; i++) {
      const answer = answers[i];
      date.setTime((answer.getCollectedAt().valueOf() * 1000));
      const day = date.getDay();
      weekday[day] = weekday[day] + 1;
    }

    return weekday;
  }

  /**
   * Calculating the hours (10, 10:30 etc.) of Users
   * @param answers Answersheets of users
   */
  public calcAverageHours(answers: Array<Answersheet>) {
    const hours = [];
    const date = new Date();

    // Initialize Array
    for (let i = 0; i < 48; i++) {
      hours.push(0);
    }

    for (let i = 0; i < answers.length; i++) {
      const answer = answers[i];
      date.setTime((answer.getCollectedAt().valueOf() * 1000));
      const hour = date.getHours();
      const minutes = date.getMinutes();

      let index = (hour - 1) * 2; // (1-1)*2 = 0, (2-1) * 2 = 2, (3-1)*2 = 4

      if (minutes >= 30) {
        index = index + 1; // 2:31 => 2+1 = 3; 12:24 => 22; 12:30 => 23
      }

      hours[index] = hours[index] + 1;
    }

    return hours;
  }

  /**
   * Calculate the yes no answers of a certain questionnaire
   * @param element Questionnaire element
   * @param answers answersheets
   */
  public calcYesNoAnswers(element: QuestionnaireElementYesNo, answers: Array<Answersheet>) {
    const count = [0, 0];
    const hoursYes = [];
    const hoursNo = [];
    const label = element.getLabel();
    const weekdayYes = [0, 0, 0, 0, 0, 0, 0];
    const weekdayNo = [0, 0, 0, 0, 0, 0, 0];
    const date = new Date();

    let hour = 0;
    let minutes = 0;
    let index = 0;
    let day = 0;

    // Initialize Array
    for (let i = 0; i < 48; i++) {
      hoursYes.push(0);
      hoursNo.push(0);
    }

    for (let i = 0; i < answers.length; i++) {
      const answer = answers[i].getAnswers();

      if (answer instanceof Array) {
        for (let j = 0; j < answer.length; j++) {
          const a: any = answer[j];
          if (a.label === label) {
            date.setTime((a.collected_at * 1000));
            hour = date.getHours();
            minutes = date.getMinutes();
            day = date.getDay();
            // Answer for label found
            if (a.value === 'YES') {
              count[0] = count[0] + 1;

              index = (hour - 1) * 2; // (1-1)*2 = 0, (2-1) * 2 = 2, (3-1)*2 = 4
              if (minutes >= 30) {
                index = index + 1; // 2:31 => 2+1 = 3; 12:24 => 22; 12:30 => 23
              }
              hoursYes[index] = hoursYes[index] + 1;
              weekdayYes[day] = weekdayYes[day] + 1;
            } else {
              count[1] = count[1] + 1;
              index = (hour - 1) * 2; // (1-1)*2 = 0, (2-1) * 2 = 2, (3-1)*2 = 4
              if (minutes >= 30) {
                index = index + 1; // 2:31 => 2+1 = 3; 12:24 => 22; 12:30 => 23
              }
              hoursNo[index] = hoursNo[index] + 1;
              weekdayNo[day] = weekdayNo[day] + 1;
            }
          }
        }
      }
    }

    const ret = {
      'count': count,
      'hoursYes': hoursYes,
      'hoursNo': hoursNo,
      'weekdayYes': weekdayYes,
      'weekdayNo': weekdayNo
    };

    return ret;
  }

  /**
   * This function calculates the statistical values of a QuestionnaireElementSingleChoice Element
   * @param element Element to get analaysed
   * @param answers Answers to get be analaysed
   */
  public calcSingleChoiceAnswers(element: QuestionnaireElementSingleChoice, answers: Array<Answersheet>) {
    const valNumberMap = {};
    const count = [];
    const weekday = [];
    const hours = [];

    const date = new Date();

    let hour = 0;
    let minutes = 0;
    let index = 0;
    let day = 0;

    // Initialize all elements
    for (let i = 0; i < element.getValues().length; i++) {
      valNumberMap[element.getValues()[i]] = i;
      count.push(0);

      const week = [0, 0, 0, 0, 0, 0, 0];
      weekday.push(week);

      const h = [];
      for (let j = 0; j < 48; j++) {
        h.push(0);
      }

      hours.push(h);
    }

    for (let i = 0; i < answers.length; i++) {
      const answer = answers[i].getAnswers();

      if (!(answer instanceof Array)) { continue; }

      for (let j = 0; j < answer.length; j++) {
        const a: any = answer[j];

        if (a.label !== element.getLabel()) { continue; }

        if (valNumberMap[a.value] !== undefined) {
          const n = valNumberMap[a.value];
          count[n] = count[n] + 1;

          date.setTime((a.collected_at * 1000));
          hour = date.getHours();
          minutes = date.getMinutes();
          day = date.getDay();

          index = (hour - 1) * 2; // (1-1)*2 = 0, (2-1) * 2 = 2, (3-1)*2 = 4
          if (minutes >= 30) {
            index = index + 1; // 2:31 => 2+1 = 3; 12:24 => 22; 12:30 => 23
          }
          hours[n][index] = hours[n][index] + 1;
          weekday[n][day] = weekday[n][day] + 1;
        }
      }
    }

    const ret = {
      'count': count,
      'weekday': weekday,
      'hours': hours
    };

    return ret;
  }

  /**
   * This function calculates the data of a slider element
   * @param element QuestionnaireElementSlider element of questionnaire
   * @param answers Answersheets of all users
   */
  public calcSliderAnswers(element: QuestionnaireElementSlider, answers: Array<Answersheet>) {
    const ranges = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    const hours = [];
    const hoursCount = [];
    const hoursSum = [];

    const date = new Date();
    let hour = 0;

    for (let i = 0; i < 24; i++) {
      hours.push(0);
      hoursCount.push(0);
      hoursSum.push(0);
    }

    for (let i = 0; i < answers.length; i++) {
      const answer = answers[i].getAnswers();

      if (!(answer instanceof Array)) { continue; }

      for (let j = 0; j < answer.length; j++) {
        const a: any = answer[j];

        if (a.label !== element.getLabel()) { continue; }

        date.setTime((a.collected_at * 1000));
        hour = date.getHours();

        // Calc ranges
        a.value = +a.value;
        if (a.value > 100 || a.value === undefined || a.value === null) {
          a.value = 100;
        }

        const r = (Math.round((a.value / 10))) - 1; // 49/10 - 1 = 4; 11/10 -1 = 0;
        ranges[r] = ranges[r] + 1;

        if (a.value !== undefined && a.value !== null && a.value >= 0) {
          hoursCount[hour] = hoursCount[hour] + 1;
          hoursSum[hour] = hoursSum[hour] + a.value;
          hours[hour] = hoursSum[hour] / hoursCount[hour];

          if (hours[hour] > 100) {
            hours[hour] = 100;
          }
        }
      }
    }

    const ret = {
      'ranges': ranges,
      'hours': hours
    }

    return ret;
  }

  /**
   * This function calculates all statistics for multiple questions answers
   * @param element Element of multipleChoice Questions
   * @param answers all Anwers (will be filtered by the right question)
   */
  public calcMultipleAnswers(element: QuestionnaireElementMultipleChoice, answers: Array<Answersheet>) {
    const valNumberMap = {};
    const hours = [];
    const days = [];
    const values = [];
    const elemValues = element.getValues();

    const date = new Date();
    let hour, minutes, day, index;

    for (let s = 0; s < elemValues.length; s++) {
      valNumberMap[elemValues[s]] = s;
      values.push(0);

      const week = [0, 0, 0, 0, 0, 0, 0];
      days.push(week);

      const h = [];
      for (let j = 0; j < 48; j++) {
        h.push(0);
      }
      hours.push(h);
    }

    for (let i = 0; i < answers.length; i++) {
      const answer = answers[i].getAnswers();

      if (!(answer instanceof Array)) { continue; }

      for (let j = 0; j < answer.length; j++) {
        const a: any = answer[j];
        if (a.label !== element.getLabel()) { continue; }
        date.setTime((a.collected_at * 1000));
        hour = date.getHours();
        minutes = date.getMinutes();
        day = date.getDay();

        for (let s = 0; s < a.value.length; s++) {
          const aV = a.value[s];

          if (valNumberMap[aV] === undefined) { continue; }
          const n = valNumberMap[aV];

          index = (hour - 1) * 2; // (1-1)*2 = 0, (2-1) * 2 = 2, (3-1)*2 = 4
          if (minutes >= 30) {
            index = index + 1; // 2:31 => 2+1 = 3; 12:24 => 22; 12:30 => 23
          }

          hours[n][index] = hours[n][index] + 1;
          days[n][day] = days[n][day] + 1;

          values[n] = values[n] + 1;
        }

      }
    }

    const ret = {
      'hours': hours,
      'days': days,
      'values': values
    };

    return ret;
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/hcpstatistic.service.ts